import React, { useState, useEffect } from "react";
import { Heart } from "lucide-react";
import { usePublicAuth } from "../context/PublicAuthContext";

export default function VoteButton({ release, size = 16, showCount = true, className = "", onVoteChange }) {
  const { isPublicAuth, publicApi, login, interactionCache, updateInteractionCache } = usePublicAuth();

  const now = new Date();
  const storageKey = `huwnd_voted_${release.id}_${now.getFullYear()}_${now.getMonth() + 1}`;

  const cached = interactionCache[release.id] || {};
  const [voted, setVoted] = useState(() => {
    if (cached.voted !== undefined) return cached.voted;
    return Boolean(localStorage.getItem(storageKey));
  });
  const [count, setCount] = useState(cached.voteCount ?? (release.vote_count || 0));
  const [loading, setLoading] = useState(false);
  const [animating, setAnimating] = useState(false);

  // Sync state when global cache changes (e.g., another instance voted)
  useEffect(() => {
    const c = interactionCache[release.id];
    if (!c) return;
    if (c.voted !== undefined) setVoted(c.voted);
    if (c.voteCount !== undefined) setCount(c.voteCount);
  }, [interactionCache[release.id]?.voted, interactionCache[release.id]?.voteCount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const c = interactionCache[release.id];
    if (c?.voteCount === undefined) setCount(release.vote_count || 0);
  }, [release.vote_count]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVote = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (!isPublicAuth) {
      login({ appState: { returnTo: window.location.pathname } });
      return;
    }
    if (loading) return;

    setLoading(true);
    setAnimating(true);
    try {
      const res = await publicApi("post", `/votes/${release.id}`);
      const newVoted = res.data.voted;
      const newCount = res.data.vote_count;
      setCount(newCount);
      setVoted(newVoted);
      if (newVoted) {
        localStorage.setItem(storageKey, Date.now().toString());
      } else {
        localStorage.removeItem(storageKey);
      }
      updateInteractionCache(release.id, { voted: newVoted, voteCount: newCount });
      if (onVoteChange) onVoteChange(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        login({ appState: { returnTo: window.location.pathname } });
      }
    } finally {
      setLoading(false);
      setTimeout(() => setAnimating(false), 400);
    }
  };

  return (
    <button
      onClick={handleVote}
      disabled={loading}
      data-testid={`vote-btn-${release.id}`}
      title={isPublicAuth ? (voted ? "Vote entfernen" : "Voten") : "Anmelden zum Voten"}
      className={`flex items-center gap-1.5 transition-transform cursor-pointer group ${className}`}
      style={{
        transform: animating ? "scale(1.4)" : "scale(1)",
        transition: "transform 0.3s cubic-bezier(.36,.07,.19,.97)",
      }}
    >
      <Heart
        size={size}
        className={`transition-colors duration-200 ${
          voted ? "text-rose-500" : "text-zinc-600 group-hover:text-rose-400"
        }`}
        fill={voted ? "currentColor" : "none"}
      />
      {showCount && (
        <span className={`text-xs font-semibold tabular-nums ${voted ? "text-rose-400" : "text-zinc-600"}`}>
          {count}
        </span>
      )}
    </button>
  );
}
